import { z } from 'zod';

import { contactSchema } from './contacts.ts';
import { PROPERTY_TYPES, type PropertyType } from './properties.ts';

/*
 * Deals (opportunities): from the lead to won or lost, in a pipeline of stages.
 * Rules shared by the API and the web forms. Messages are shown to users (Spanish).
 */

/** What the client wants to do (the property side is sale/rent). */
/** Lost reason of the open deals of a contact anonymized at their request. */
export const HOLDER_REQUEST_LOST_REASON = 'Solicitud del titular';

/** listing: an owner who wants to sell or rent through the agency ("captación"). */
export const DEAL_OPERATIONS = {
  purchase: 'Compra',
  rent: 'Alquiler',
  listing: 'Captación',
} as const;
export type DealOperation = keyof typeof DEAL_OPERATIONS;

export const DEAL_STATUSES = { open: 'Abierto', won: 'Ganado', lost: 'Perdido' } as const;
export type DealStatus = keyof typeof DEAL_STATUSES;

export const DEAL_PROPERTY_STATUSES = {
  suggested: 'Sugerida',
  visited: 'Visitada',
  favorite: 'Favorita',
  discarded: 'Descartada',
} as const;
export type DealPropertyStatus = keyof typeof DEAL_PROPERTY_STATUSES;

export const PARTICIPANT_ROLES = { co_buyer: 'Co-comprador', referrer: 'Referidor' } as const;
export type ParticipantRole = keyof typeof PARTICIPANT_ROLES;

/** Pipelines every new agency starts with (editable; no "Reserva": it comes with Won). */
export const DEFAULT_PIPELINES = [
  {
    name: 'Ventas',
    operation: 'purchase',
    stages: [
      { name: 'Nuevo', probability: 10, staleDays: 2 },
      { name: 'Contactado', probability: 20, staleDays: 5 },
      { name: 'Visitas', probability: 40, staleDays: 10 },
      { name: 'Negociación', probability: 70, staleDays: 10 },
    ],
  },
  {
    name: 'Alquileres',
    operation: 'rent',
    stages: [
      { name: 'Nuevo', probability: 10, staleDays: 2 },
      { name: 'Contactado', probability: 25, staleDays: 3 },
      { name: 'Visitas', probability: 50, staleDays: 5 },
      { name: 'Negociación', probability: 80, staleDays: 5 },
    ],
  },
  {
    name: 'Captaciones',
    operation: 'listing',
    stages: [
      { name: 'Nuevo', probability: 10, staleDays: 2 },
      { name: 'Contactado', probability: 25, staleDays: 3 },
      { name: 'Visita de valoración', probability: 50, staleDays: 7 },
      { name: 'Firma del acuerdo', probability: 80, staleDays: 7 },
    ],
  },
] as const satisfies readonly {
  name: string;
  operation: DealOperation;
  stages: readonly { name: string; probability: number; staleDays: number | null }[];
}[];

export const DEFAULT_LOST_REASONS = [
  'Precio fuera de su presupuesto',
  'Compró o alquiló con otra inmobiliaria',
  'No respondió',
  'Ya no busca',
  'No califica para financiamiento',
  'La propiedad ya no está disponible',
  'Duplicado',
  'Otro',
] as const;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Máximo ${String(max)} caracteres`)
    .transform((v) => (v === '' ? null : v))
    .nullable();

const money = z
  .number('Escribe un número')
  .min(0, 'No puede ser negativo')
  .max(999_999_999_999, 'Monto demasiado alto')
  .multipleOf(0.01, 'Máximo 2 decimales')
  .nullable();

/** What the client is looking for (HU-CRM-14). */
export const dealInterestSchema = z
  .strictObject({
    propertyTypes: z
      .array(z.enum(Object.keys(PROPERTY_TYPES) as [PropertyType]))
      .max(6)
      .default([]),
    districtIds: z.array(z.number().int().positive()).max(30).default([]),
    budgetMin: money.default(null),
    budgetMax: money.default(null),
    bedroomsMin: z.number().int().min(0).max(50).nullable().default(null),
    bathroomsMin: z.number().min(0).max(50).multipleOf(0.5).nullable().default(null),
    parkingMin: z.number().int().min(0).max(50).nullable().default(null),
  })
  .refine((v) => v.budgetMin === null || v.budgetMax === null || v.budgetMax >= v.budgetMin, {
    path: ['budgetMax'],
    message: 'Debe ser mayor o igual al mínimo',
  });
export type DealInterest = z.output<typeof dealInterestSchema>;

const utm = z.string().trim().max(100).optional();

/** A deal for an existing contact (the lead form creates the contact first). */
export const dealSchema = z.strictObject({
  contactId: z.uuid('Elige el contacto'),
  title: optionalText(150).optional(),
  operation: z.enum(Object.keys(DEAL_OPERATIONS) as [DealOperation]),
  /** Default: the default pipeline of the operation, first stage. */
  pipelineId: z.uuid().optional(),
  stageId: z.uuid().optional(),
  estimatedAmount: money.default(null),
  channelId: z.uuid('Canal no válido').nullable().default(null),
  assignedMembershipId: z.uuid('Agente no válido').optional(),
  interest: dealInterestSchema.default({
    propertyTypes: [],
    districtIds: [],
    budgetMin: null,
    budgetMax: null,
    bedroomsMin: null,
    bathroomsMin: null,
    parkingMin: null,
  }),
  utmSource: utm,
  utmMedium: utm,
  utmCampaign: utm,
});
export type DealInput = z.input<typeof dealSchema>;
export type DealData = z.output<typeof dealSchema>;

/**
 * HU-CRM-01: a lead by hand. Either an existing contact (the form proposes it when the
 * phone or email already exists) or a new one, plus the deal, in one step.
 */
export const leadSchema = z
  .strictObject({
    contactId: z.uuid().optional(),
    newContact: contactSchema.optional(),
    deal: dealSchema.omit({ contactId: true }),
  })
  .refine((v) => (v.contactId === undefined) !== (v.newContact === undefined), {
    message: 'Elige un contacto existente o escribe los datos del nuevo',
    path: ['contactId'],
  });
export type LeadInput = z.input<typeof leadSchema>;
export type LeadData = z.output<typeof leadSchema>;

/** Editing: the contact, operation and pipeline stay (moving uses its own routes). */
export const dealUpdateSchema = z.strictObject({
  title: z.string().trim().min(1, 'El título es obligatorio').max(150),
  estimatedAmount: money,
  channelId: z.uuid('Canal no válido').nullable(),
  interest: dealInterestSchema,
});
export type DealUpdateInput = z.infer<typeof dealUpdateSchema>;

export const dealListQuerySchema = z.strictObject({
  q: z.string().trim().max(100).optional(),
  pipelineId: z.uuid().optional(),
  stageId: z.uuid().optional(),
  status: z.enum(Object.keys(DEAL_STATUSES) as [DealStatus]).optional(),
  channelId: z.uuid().optional(),
  assignedMembershipId: z.uuid().optional(),
  contactId: z.uuid().optional(),
  createdFrom: z.iso.date().optional(),
  createdTo: z.iso.date().optional(),
  stale: z.enum(['true']).optional(),
  /** HU-CRM-18: open deals with nothing scheduled. */
  noNextActivity: z.enum(['true']).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type DealListQuery = z.infer<typeof dealListQuerySchema>;

export const dealBoardQuerySchema = z.strictObject({
  pipelineId: z.uuid(),
  assignedMembershipId: z.uuid().optional(),
});

export const dealStageSchema = z.strictObject({ stageId: z.uuid() });
export const dealWinSchema = z.strictObject({ note: optionalText(1000).optional() });
export const dealLoseSchema = z.strictObject({
  reasonId: z.uuid('Elige el motivo'),
  comment: optionalText(1000).optional(),
});
export const dealReopenSchema = z.strictObject({ stageId: z.uuid().optional() });
export const dealReassignSchema = z.strictObject({
  dealIds: z.array(z.uuid()).min(1).max(500),
  toMembershipId: z.uuid('Elige a quién'),
  reason: z.string().trim().min(3, 'Indica el motivo').max(300),
});
export type DealReassignInput = z.infer<typeof dealReassignSchema>;

export const dealNoteSchema = z.strictObject({
  body: z.string().trim().min(1, 'Escribe la nota').max(5000),
  /** Colleagues chosen with @ (membership ids). */
  mentions: z.array(z.uuid()).max(20).default([]),
});
export type DealNoteInput = z.input<typeof dealNoteSchema>;

export const dealPropertySchema = z.strictObject({
  propertyId: z.uuid(),
  status: z.enum(Object.keys(DEAL_PROPERTY_STATUSES) as [DealPropertyStatus]).default('suggested'),
});
export const dealPropertyStatusSchema = z.strictObject({
  status: z.enum(Object.keys(DEAL_PROPERTY_STATUSES) as [DealPropertyStatus]),
});
export const dealParticipantSchema = z.strictObject({
  contactId: z.uuid('Elige el contacto'),
  role: z.enum(Object.keys(PARTICIPANT_ROLES) as [ParticipantRole]),
});

export const TIMELINE_KINDS = {
  note: 'Notas',
  activity: 'Actividades',
  stage: 'Etapas',
  status: 'Ganado y perdido',
  property: 'Propiedades',
  other: 'Otros',
} as const;
export type TimelineKind = keyof typeof TIMELINE_KINDS;

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export const pipelineSchema = z.strictObject({
  name: z.string().trim().min(1, 'Escribe el nombre').max(60),
  operation: z.enum(Object.keys(DEAL_OPERATIONS) as [DealOperation]),
});
export const pipelineUpdateSchema = z
  .strictObject({
    name: z.string().trim().min(1, 'Escribe el nombre').max(60).optional(),
    isDefault: z.literal(true).optional(),
    archived: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'No hay cambios');

export const stageSchema = z.strictObject({
  name: z.string().trim().min(1, 'Escribe el nombre').max(60),
  probability: z.number().int().min(0).max(100),
  staleDays: z.number().int().min(1).max(365).nullable(),
});
export const stageUpdateSchema = stageSchema
  .partial()
  .extend({ archived: z.boolean().optional() })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'No hay cambios');
export const stageOrderSchema = z.strictObject({ ids: z.array(z.uuid()).min(1).max(30) });

export const lostReasonSchema = z.strictObject({
  name: z.string().trim().min(1, 'Escribe el motivo').max(80),
});
export const lostReasonUpdateSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(80).optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'No hay cambios');

/** Deal title when none is given: "Compra · María Hernández". */
export function defaultDealTitle(operation: DealOperation, contactName: string): string {
  return `${DEAL_OPERATIONS[operation]} · ${contactName}`.slice(0, 150);
}
